import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion } from '@cbhq/cds-web/accordion/Accordion';
import { AccordionItem } from '@cbhq/cds-web/accordion/AccordionItem';
import { Banner } from '@cbhq/cds-web/alpha/banner/Banner';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { Table } from '@cbhq/cds-web/tables/Table';
import { TableBody } from '@cbhq/cds-web/tables/TableBody';
import { TableCell } from '@cbhq/cds-web/tables/TableCell';
import { TableHeader } from '@cbhq/cds-web/tables/TableHeader';
import { TableRow } from '@cbhq/cds-web/tables/TableRow';
import { Link, TextBody, TextHeadline, TextLabel2, TextTitle1 } from '@cbhq/cds-web/typography';
import { abbreviateHash } from '@cbhq/commerce-shared/utils/helper';

import { AnalyticEvent } from ':fat/analytics/events';
import { logRender } from ':fat/analytics/logActions';
import { logFundsAccessCriticalStep } from ':fat/analytics/logMetrics';
import { checkTransactions } from ':fat/api/checkTransactions';
import { SingleColumnLayout } from ':fat/components/PageLayout/SingleColumnLayout';
import { ALL_NETWORKS, CURRENCY_DATA, Network } from ':fat/core/networks';
import { getBroadcastsForFingerprint } from ':fat/database/queries';
import { Broadcast } from ':fat/database/schema-types';
import { errorToString } from ':fat/helpers/errors';
import { formatCrypto, formatFiat } from ':fat/helpers/formatting';
import { convertToFiat, useFiatBalance, useRates } from ':fat/hooks/useBalances';
import { useInterval } from ':fat/hooks/useInterval';
import { reportError } from ':fat/init/bugsnag';
import { clearAllData, query } from ':fat/messaging/entities/databaseWorker/actions';
import { StatusIcon } from ':fat/pages/Status/components/StatusIcon';
import { messages } from ':fat/pages/Status/StatusPageMessages';
import { AppRoutes } from ':fat/routing/routes';

type NetworkBroadcastsAccordionProps = {
  network: Network;
  broadcasts: Broadcast[];
};

function NetworkBroadcastsAccordion({ network, broadcasts }: NetworkBroadcastsAccordionProps) {
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const { formatMessage } = useIntl();

  const totalInput = broadcasts.reduce((total, b) => total + BigInt(b.input_value), 0n);
  const totalInputFiat = useFiatBalance(network, totalInput);
  const sortedBroadcasts = broadcasts.toSorted((a, b) => {
    const diff = BigInt(b.input_value) - BigInt(a.input_value);
    return diff === 0n ? 0 : diff < 0n ? -1 : 1;
  });
  const hashes = broadcasts.map((b) => b.tx_hash);
  const anyFailed = broadcasts.some((b) => b.status === 'FAILED');
  const allSuccessful = broadcasts.every((b) => b.status === 'SUCCESS' && confirmed[b.tx_hash]);
  const allBroadcasted = broadcasts.every((b) => b.status === 'SUCCESS');

  useEffect(() => {
    if (allBroadcasted) {
      logFundsAccessCriticalStep({ step: 'broadcasted_withdrawal' });
    }
  }, [allBroadcasted]);

  useEffect(() => {
    if (allSuccessful) {
      logFundsAccessCriticalStep({ step: 'confirmed_withdrawal' });
    }
  }, [allSuccessful]);

  useInterval(() => {
    if (hashes.every((h) => confirmed[h])) {
      return;
    }

    checkTransactions(network, hashes)
      .then((results) => {
        setConfirmed(results);
      })
      .catch((err) => {
        reportError(err as Error, {
          context: 'checkTransactions',
          metadata: [
            {
              name: 'details',
              data: {
                hashes,
                network,
                error: errorToString(err),
              },
            },
          ],
        });
      });
  }, 10000);

  if (!broadcasts?.length) {
    return null;
  }

  return (
    <Box bordered borderRadius="rounded" width="100%">
      <Accordion>
        <AccordionItem
          key={`list--${network}`}
          itemKey={network}
          // @ts-expect-error AccordionItem title prop is string typed but wrapped around a div element
          title={
            <HStack justifyContent="space-between" alignItems="center">
              <VStack>
                <VStack>
                  <TextHeadline as="p">{CURRENCY_DATA[network].name}</TextHeadline>
                  {anyFailed ? (
                    <TextBody as="p" color="negative">
                      {formatMessage(messages.error)}
                    </TextBody>
                  ) : allSuccessful ? (
                    <TextBody as="p" color="positive">
                      {formatMessage(messages.success)}
                    </TextBody>
                  ) : (
                    <TextBody as="p" color="foregroundMuted">
                      {formatMessage(messages.pending)}
                    </TextBody>
                  )}
                </VStack>
              </VStack>
              <HStack gap={2} alignItems="center">
                <VStack alignItems="flex-end">
                  <TextBody as="p">{formatFiat(totalInputFiat)}</TextBody>
                  <TextBody color="foregroundMuted" as="p">
                    {formatCrypto(network, totalInput)}
                  </TextBody>
                </VStack>
              </HStack>
            </HStack>
          }
          media={
            <Avatar
              alt={`${CURRENCY_DATA[network].name} logo`}
              src={CURRENCY_DATA[network].icon}
              size="xl"
            />
          }
        >
          <Table bordered variant="ruled">
            <TableHeader sticky>
              <TableRow backgroundColor="secondary">
                <TableCell title={formatMessage(messages.transactionHash)} width="50%" />
                <TableCell
                  title={formatMessage(messages.value)}
                  width="35%"
                  alignItems="flex-end"
                />
                <TableCell title={formatMessage(messages.status)} justifyContent="center" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBroadcasts.map((broadcast) => (
                <TableRow key={`row--${broadcast.tx_hash}`}>
                  <TableCell>
                    <HStack gap={2} alignItems="center">
                      <Link to={explorerURL(network, broadcast)} underline={false} openInNewWindow>
                        {abbreviateHash(broadcast.tx_hash, 6, 6)}
                      </Link>
                      <Icon name="externalLink" size="s" color="primary" />
                    </HStack>
                  </TableCell>
                  <TableCell alignItems="flex-end">
                    <TextBody as="p">
                      {formatCrypto(network, BigInt(broadcast.input_value))}
                    </TextBody>
                  </TableCell>
                  <TableCell direction="horizontal" justifyContent="center">
                    {broadcast.status === 'FAILED' && broadcast.failure_reason ? (
                      <Tooltip content={broadcast.failure_reason}>
                        <StatusIcon status="FAILED" />
                      </Tooltip>
                    ) : (
                      <StatusIcon
                        status={
                          confirmed[broadcast.tx_hash]
                            ? 'COMPLETE'
                            : broadcast.status === 'FAILED'
                            ? 'FAILED'
                            : 'PENDING'
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}

export function StatusPage() {
  const { formatMessage } = useIntl();
  const { fingerprint } = useParams();
  const navigate = useNavigate();
  const broadcasts = useBroadcasts(fingerprint);
  const rates = useRates();

  const anyFailures = Object.values(broadcasts)
    .flat()
    .some((b) => b.status === 'FAILED');
  const totalProceedsFiat = !rates
    ? 0
    : Object.values(broadcasts)
        .flat()
        .filter((b) => b.status !== 'FAILED')
        .reduce(
          (total, b) => total + convertToFiat(b.network as Network, BigInt(b.dest_value), rates),
          0,
        );

  const handleTryAgain = useCallback(() => {
    void clearAllData().then(() => {
      navigate(AppRoutes.SECURITY_FRAME_LOADER);
    });
  }, [navigate]);

  useEffect(() => {
    logRender(AnalyticEvent.StatusPageRendered);
  }, []);

  return (
    <SingleColumnLayout>
      <VStack alignItems="center" justifyContent="center" spacing={4} maxWidth={640}>
        <VStack gap={2} alignItems="center" spacing={4}>
          <Pictogram name="clock" dimension="64x64" />
          <TextTitle1 as="h1">{formatMessage(messages.title)}</TextTitle1>
          <TextBody as="p" align="center" color="foregroundMuted">
            {formatMessage(messages.description)}
          </TextBody>
        </VStack>
        <VStack gap={3} width="100%">
          {anyFailures && (
            <Banner
              startIcon="info"
              styleVariant="contextual"
              title={formatMessage(messages.failuresPresentBannerTitle)}
              variant="promotional"
              primaryAction={
                <Button compact transparent onPress={handleTryAgain}>
                  {formatMessage(messages.failuresPresentBannerAction)}
                </Button>
              }
            >
              <TextLabel2 as="p">{formatMessage(messages.failuresPresentBannerBody)}</TextLabel2>
            </Banner>
          )}
          <VStack gap={2} width="100%">
            {ALL_NETWORKS.map((network) =>
              broadcasts[network]?.length ? (
                <NetworkBroadcastsAccordion
                  key={network}
                  network={network}
                  broadcasts={broadcasts[network]}
                />
              ) : null,
            )}
          </VStack>
        </VStack>

        <VStack gap={3} spacingTop={6} width="100%">
          <HStack justifyContent="space-between">
            <HStack alignItems="center">
              <TextHeadline as="p">{formatMessage(messages.totalProceeds)}</TextHeadline>
              <Box spacingStart={2}>
                <Tooltip content={formatMessage(messages.totalEstimatedProceedsTooltip)}>
                  <Icon size="xs" name="info" color="foreground" />
                </Tooltip>
              </Box>
            </HStack>
            <TextHeadline as="p">{formatFiat(totalProceedsFiat)}</TextHeadline>
          </HStack>
        </VStack>
      </VStack>
    </SingleColumnLayout>
  );
}

export function useBroadcasts(fingerprint?: string) {
  const [broadcasts, setBroadcasts] = useState<Record<Network, Broadcast[]>>(
    ALL_NETWORKS.reduce(
      (all, network) => ({ ...all, [network]: [] }),
      {} as Record<Network, Broadcast[]>,
    ),
  );

  useEffect(() => {
    const poller = setInterval(poll, 100);

    function poll() {
      if (window.SANITY_CHECK_COMPLETE && typeof fingerprint === 'string') {
        void query(getBroadcastsForFingerprint(parseInt(fingerprint))).then((bs) => {
          setBroadcasts(
            bs.reduce(
              (all, b) => ({
                ...all,
                [b.network as Network]: [...(all[b.network as Network] ?? []), b],
              }),
              {} as Record<Network, Broadcast[]>,
            ),
          );
          clearInterval(poller);
        });
      }
    }

    return () => {
      clearInterval(poller);
    };
  }, [fingerprint]);

  return broadcasts;
}

function explorerURL(network: Network, broadcast: Broadcast) {
  const hash = broadcast.tx_hash;
  const testnet = window.DEVTOOL_STATE?.testnet;
  switch (network + (testnet ? '-testnet' : '-mainnet')) {
    case 'btc-mainnet':
      return `https://mempool.space/tx/${hash}`;
    case 'btc-testnet':
      return `https://mempool.space/testnet/tx/${hash}`;
    case 'ltc-mainnet':
      return `https://chain.so/tx/LTC/${hash}`;
    case 'ltc-testnet':
      return `https://chain.so/tx/LTCTEST/${hash}`;
    case 'doge-mainnet':
      return `https://chain.so/tx/DOGE/${hash}`;
    case 'doge-testnet':
      return `https://chain.so/tx/DOGETEST/${hash}`;
    case 'bch-mainnet':
      return `https://explorer.btc.com/bch/transaction/${hash}`;
    case 'bch-testnet':
      return `https://blockexplorer.one/bitcoin-cash/testnet/tx/${hash}`;
    case 'eth-mainnet':
      return `https://etherscan.io/tx/${hash}`;
    case 'eth-testnet':
      return `https://sepolia.etherscan.io/tx/${hash}`;
    default:
      return '#';
  }
}
